"use client";

import Link from "next/link";
import Image from "next/image";
import { CopyrightYear } from "./copyright-year";
import { BrandMark } from "@/components/ui/brand-mark";
import { branding } from "@/lib/branding.config";
import { legal } from "@/lib/legal.config";
import { compliance, contactPhoneHref } from "@/lib/compliance.config";
import { GPU_TYPES, productsHref } from "@/lib/catalog/filters";

// Legal entity is per-project — edit `lib/legal.config.ts` to change.
const companyInfo = {
  legalName: legal("name"),
  registrationNumber: legal("registration"),
  address: legal("address"),
  email: compliance.contactEmail,
};

// Footer sections data
// GPU_TYPES holds the rentable GPUs only (withdrawn ones are filtered out in
// lib/catalog/filters), so this column cannot list a card nobody can rent.
const gpuLinks = [
  ...GPU_TYPES.map(({ value, label }) => ({
    title: label,
    href: productsHref({ gpu: value }),
  })),
  { title: "All Cloud PCs", href: productsHref() },
];

// This column once linked `?useCase=gaming|rendering|…`, but `useCase` is not
// a param anything reads, so every link landed on the unfiltered grid. It
// lists the informational pages instead.
const productLinks = [
  { title: "Pricing", href: "/pricing" },
  { title: "How It Works", href: "/how-it-works" },
  { title: "Requirements", href: "/requirements" },
];

const supportLinks = [
  { title: "FAQ", href: "/faq" },
  { title: "Help & Contact", href: "/help" },
];

const accountLinks = [
  { title: "Dashboard", href: "/dashboard" },
  { title: "My Machines", href: "/machines" },
  { title: "Wallet", href: "/wallet" },
];

// Acquirers check for delivery, refund and dispute policies in the footer.
// They are sections of the Terms; the ids are the section ids in
// content/legal/terms-of-service.ts (the /refund-policy and /dispute-policy
// permalinks redirect to the same anchors).
const legalLinks = [
  { title: "Terms of Service", href: "/terms" },
  { title: "Privacy Policy", href: "/privacy" },
  { title: "Delivery Policy", href: "/terms#delivery-and-availability" },
  { title: "Refund Policy", href: "/terms#refunds" },
  { title: "Dispute Policy", href: "/terms#dispute-resolution" },
];

// The chapters of the closing ledger, numbered like the docket sections above.
const chapters = [
  { title: "Cloud PCs", links: gpuLinks },
  { title: "Product", links: productLinks },
  { title: "Support", links: supportLinks },
  { title: "Account", links: accountLinks },
  { title: "Legal", links: legalLinks },
];

function Chapter({
  index,
  title,
  links,
}: {
  index: number;
  title: string;
  links: { title: string; href: string }[];
}) {
  return (
    <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-baseline gap-x-4 border-b border-foreground/20 py-4 sm:grid-cols-[5.5rem_9rem_minmax(0,1fr)]">
      <span className="font-heading text-5xl font-bold leading-none tabular-nums tracking-tight text-foreground">
        {String(index + 1).padStart(2, "0")}.
      </span>
      <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-foreground">{title}</h3>
      <ul className="col-span-2 mt-3 flex flex-wrap gap-x-5 gap-y-2 sm:col-span-1 sm:mt-0">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              href={link.href}
              className="text-sm text-foreground underline-offset-4 transition-colors hover:underline"
            >
              {link.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="border-t-2 border-foreground bg-background">
      {/* Ledger header strip */}
      <div className="border-b border-foreground/20">
        <div className="mx-auto flex w-full max-w-[1200px] flex-wrap items-center gap-x-3 gap-y-1 px-5 py-2.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground md:px-6">
          <span className="font-bold text-foreground">REF END</span>
          <span aria-hidden>{"//"}</span>
          <span>Closing ledger</span>
          <span aria-hidden>{"//"}</span>
          <span className="normal-case">{branding.domain}</span>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1200px] px-5 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-x-6">
          {/* Margin: filed by */}
          <aside className="py-6 lg:col-span-3 lg:border-r lg:border-foreground/20 lg:py-8 lg:pr-6">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-foreground">[ Filed by ]</p>
            <Link href="/" className="group mt-4 inline-flex">
              <BrandMark size="default" />
            </Link>
            <p className="mt-4 max-w-[280px] text-xs leading-relaxed text-foreground">
              Windows cloud PCs with NVIDIA GPUs for gaming, rendering, AI and compute. Hourly rates,
              metered per minute.
            </p>
            <div className="mt-5 space-y-1.5 border-t border-foreground/20 pt-4" data-nosnippet>
              <a
                href={`mailto:${companyInfo.email}`}
                className="block break-all text-xs font-bold text-foreground hover:underline"
              >
                {companyInfo.email}
              </a>
              {compliance.contactPhone && (
                <a href={contactPhoneHref!} className="block text-xs text-foreground hover:underline">
                  {compliance.contactPhone}
                </a>
              )}
            </div>
          </aside>

          {/* Body: numbered chapters */}
          <div className="border-t border-foreground/20 lg:col-span-9 lg:border-t-0 lg:py-4">
            {chapters.map((chapter, index) => (
              <Chapter key={chapter.title} index={index} title={chapter.title} links={chapter.links} />
            ))}
          </div>
        </div>
      </div>

      {/* Closing status line */}
      <div className="border-t-2 border-foreground">
        <div
          className="mx-auto flex w-full max-w-[1200px] flex-col gap-3 px-5 py-4 text-[11px] uppercase tracking-[0.1em] text-muted-foreground md:px-6 lg:flex-row lg:items-center lg:justify-between"
          data-nosnippet
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-foreground">{companyInfo.legalName}</span>
            <span aria-hidden="true">·</span>
            <span>Reg. no. {companyInfo.registrationNumber}</span>
            <span aria-hidden="true">·</span>
            <span className="normal-case">{companyInfo.address}</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* Payment Methods: the official white card marks, unaltered. */}
            <span className="flex items-center gap-2">
              <span>We accept</span>
              <Image src="/assets/icons/payment/visa.svg" alt="Visa" width={40} height={27} />
              <Image src="/assets/icons/payment/mastercard.svg" alt="Mastercard" width={40} height={27} />
            </span>
            <span aria-hidden="true">·</span>
            <span>
              &copy; <CopyrightYear /> {branding.name}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
